import { WorkItem } from '../../workitems/workItemTypes';
import { LeaveEntry, PublicHoliday } from '@techgitter/contracts';
import { ConflictWarning } from '../calendarTypes';

/**
 * Detect double-booking: two appointments for the same technician with overlapping times.
 */
export function detectDoubleBookings(
  appointment: WorkItem,
  allAppointments: WorkItem[],
): ConflictWarning[] {
  if (!appointment.startAt || !appointment.endAt) return [];

  const start = new Date(appointment.startAt).getTime();
  const end = new Date(appointment.endAt).getTime();
  const warnings: ConflictWarning[] = [];

  for (const other of allAppointments) {
    if (other.guid === appointment.guid) continue;
    if (!other.startAt || !other.endAt) continue;
    if (other.userFullName !== appointment.userFullName) continue;

    const otherStart = new Date(other.startAt).getTime();
    const otherEnd = new Date(other.endAt).getTime();

    // Overlap check: starts before other ends AND ends after other starts
    if (start < otherEnd && end > otherStart) {
      warnings.push({
        appointmentGuid: appointment.guid,
        type: 'double-booking',
        message: `Overlaps with "${other.title}" (${other.startAt})`,
      });
    }
  }

  return warnings;
}

/**
 * Detect leave overlap: appointment scheduled during a technician's leave.
 */
export function detectLeaveOverlaps(
  appointment: WorkItem,
  leaveEntries: LeaveEntry[],
): ConflictWarning[] {
  if (!appointment.startAt) return [];

  const appointmentDate = appointment.startAt.slice(0, 10); // YYYY-MM-DD
  const warnings: ConflictWarning[] = [];

  for (const leave of leaveEntries) {
    if (leave.userFullName !== appointment.userFullName) continue;

    const leaveStart = leave.startAt.slice(0, 10);
    const leaveEnd = leave.endAt.slice(0, 10);
    if (appointmentDate >= leaveStart && appointmentDate <= leaveEnd) {
      warnings.push({
        appointmentGuid: appointment.guid,
        type: 'leave-overlap',
        message: `${appointment.userFullName} is on ${leave.leaveType.toLowerCase()} leave`,
      });
    }
  }

  return warnings;
}

/**
 * Detect appointment on a public holiday.
 */
export function detectHolidayConflicts(
  appointment: WorkItem,
  holidays: PublicHoliday[],
): ConflictWarning[] {
  if (!appointment.startAt) return [];

  const appointmentDate = appointment.startAt.slice(0, 10);
  const warnings: ConflictWarning[] = [];

  for (const holiday of holidays) {
    if (holiday.date === appointmentDate) {
      warnings.push({
        appointmentGuid: appointment.guid,
        type: 'public-holiday',
        message: `Public holiday: ${holiday.name}`,
      });
    }
  }

  return warnings;
}

/**
 * Get all conflict warnings for an appointment.
 */
export function getAllConflicts(
  appointment: WorkItem,
  allAppointments: WorkItem[],
  leaveEntries: LeaveEntry[],
  holidays: PublicHoliday[],
): ConflictWarning[] {
  return [
    ...detectDoubleBookings(appointment, allAppointments),
    ...detectLeaveOverlaps(appointment, leaveEntries),
    ...detectHolidayConflicts(appointment, holidays),
  ];
}
